import React, { useState, useEffect, useCallback, useMemo } from 'react';


import { 
  fetchAllAllowedGroups, 
  createAllowedGroup, 
  updateAllowedGroup, 
  deleteAllowedGroup,
  fetchAirlines
} from '../services/allowedGroupsService';
import { fetchAdminUsers } from '../services/usersService';
import { AllowedGroup } from '../models/allowedGroup';
import { useAuth } from '../context/AuthContext';
import { useUserDataAuthConsumer } from '../context/UserDataContext';
import {
  AIRLINES,
  LABELS,
  BUTTON_LABELS,
  TABLE_HEADERS,
  STATUS_LABELS,
  ERROR_MESSAGES,
} from '../constants/appConstants';
import '../styles/AdminAllowedGroups.scss';

const FILTER_ALL = 'ALL';
const DEFAULT_AIRLINE = AIRLINES.AS;

const getErrorMessageFromApi = (err: unknown, fallbackMessage: string, conflictMessage: string): string => {
  if (typeof err !== 'object' || err === null) {
    return fallbackMessage;
  }

  const errorWithResponse = err as {
    response?: {
      status?: number;
      data?: {
        message?: string;
        error?: string;
        title?: string;
      } | string;
    };
    message?: string;
  };

  const statusCode = errorWithResponse.response?.status;
  const responseData = errorWithResponse.response?.data;
  const apiMessage = typeof responseData === 'string'
    ? responseData
    : responseData?.message || responseData?.error || responseData?.title;

  if (statusCode === 409) {
    return apiMessage || conflictMessage;
  }

  if (apiMessage) {
    return apiMessage;
  }

  if (errorWithResponse.message) {
    return errorWithResponse.message;
  }

  return fallbackMessage;
};

const AdminAllowedGroups: React.FC = () => {
  const [groups, setGroups] = useState<AllowedGroup[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingGroup, setEditingGroup] = useState<AllowedGroup | null>(null);
  const [airlines, setAirlines] = useState<string[]>([AIRLINES.AS, AIRLINES.QX]);
  const [selectedAirlineFilter, setSelectedAirlineFilter] = useState<string>(FILTER_ALL);
  const [adminUsers, setAdminUsers] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(10);
  const [modalFeedback, setModalFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [formData, setFormData] = useState<AllowedGroup>({
    groupName: '',
    airline: DEFAULT_AIRLINE,
    isActive: true
  });

  const userGroups = useAuth();
  const token = userGroups?.userData?.access_token || '';
  const userData = useUserDataAuthConsumer();

  const allowedAdminUsers = useMemo(() => new Set(adminUsers), [adminUsers]);
  const userEmail = useMemo(
    () => userData?.email || '',
    [userData?.email]
  );
  const isAdmin = useMemo(() => {
    const userEmailLower = userEmail.toLowerCase();
    const adminEmailsLowerCase = new Set(Array.from(allowedAdminUsers).map(email => email.toLowerCase()));
    return adminEmailsLowerCase.has(userEmailLower);
  }, [allowedAdminUsers, userEmail]);
  const isAuthLoading = userGroups.isLoading;

  useEffect(() => {
    if (!isAuthLoading && token) {
      loadAdminUsers();
    }
  }, [isAuthLoading, token]);

  useEffect(() => {
    if (isAdmin && !isAuthLoading && token) {
      loadGroups();
      loadAirlines();
    }
  }, [isAdmin, isAuthLoading, token]);

  const loadAdminUsers = useCallback(async () => {
    try {
      const admins = await fetchAdminUsers(token);
      setAdminUsers(admins);
    } catch (err) {
      console.error('Failed to load admin users:', err);
      setAdminUsers([]);
    }
  }, [token]);

  const loadGroups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllAllowedGroups(token);
      setGroups(data);
    } catch (err) {
      setError(ERROR_MESSAGES.LOAD_GROUPS_FAILED);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadAirlines = useCallback(async () => {
    try {
      const airlineList = await fetchAirlines(token);
      setAirlines(airlineList.length > 0 ? airlineList : [AIRLINES.AS, AIRLINES.QX]);
    } catch (err) {
      console.error(ERROR_MESSAGES.LOAD_AIRLINES_FAILED, err);
      setAirlines([AIRLINES.AS, AIRLINES.QX]);
    }
  }, [token]);

  const handleOpenModal = useCallback((group?: AllowedGroup) => {
    setModalFeedback(null);
    if (group) {
      setEditingGroup(group);
      setFormData(group);
    } else {
      setEditingGroup(null);
      setFormData({
        groupName: '',
        airline: airlines[0] || DEFAULT_AIRLINE,
        isActive: true
      });
    }
    setIsModalOpen(true);
  }, [airlines]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingGroup(null);
    setModalFeedback(null);
    setFormData({
      groupName: '',
      airline: airlines[0] || DEFAULT_AIRLINE,
      isActive: true
    });
  }, [airlines]);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setModalFeedback(null);

    const dataToSave = { ...formData };

    try {
      if (editingGroup?.id) {
        await updateAllowedGroup(token, editingGroup.id, dataToSave);
        setModalFeedback({ type: 'success', message: 'Group updated successfully.' });
      } else {
        await createAllowedGroup(token, dataToSave);
        setModalFeedback({ type: 'success', message: 'Group created successfully.' });
      }

      await loadGroups();
      await loadAirlines();
      globalThis.setTimeout(() => {
        handleCloseModal();
      }, 1200);
    } catch (err) {
      setModalFeedback({
        type: 'error',
        message: getErrorMessageFromApi(
          err,
          ERROR_MESSAGES.SAVE_GROUP_FAILED,
          'Group name already exists for this airline.'
        ),
      });
      console.error(err);
    }
  }, [formData, editingGroup, token, loadGroups, loadAirlines, handleCloseModal]);

  const handleDelete = useCallback(async (id: number) => {
    if (!globalThis.confirm('Are you sure you want to delete this group?')) {
      return;
    }

    setError(null);
    try {
      await deleteAllowedGroup(token, id);
      await loadGroups();
      await loadAirlines();
    } catch (err) {
      setError(ERROR_MESSAGES.DELETE_GROUP_FAILED);
      console.error(err);
    }
  }, [token, loadGroups, loadAirlines]);

  const handleToggleActive = useCallback(async (group: AllowedGroup) => {
    if (!group.id) return;

    try {
      await updateAllowedGroup(token, group.id, {
        ...group,
        isActive: !group.isActive
      });
      await loadGroups();
    } catch (err) {
      setError(ERROR_MESSAGES.UPDATE_STATUS_FAILED);
      console.error(err);
    }
  }, [token, loadGroups]);

  const uniqueAirlines = useMemo(
    () => [...new Set(groups.map(g => g.airline))].sort((a, b) => a.localeCompare(b)),
    [groups]
  );

  const filteredGroups = useMemo(
    () => selectedAirlineFilter === FILTER_ALL 
      ? groups 
      : groups.filter(g => g.airline === selectedAirlineFilter),
    [selectedAirlineFilter, groups]
  );

  // Client-side pagination
  const totalPages = Math.ceil(filteredGroups.length / itemsPerPage);
  const paginatedGroups = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredGroups.slice(startIndex, endIndex);
  }, [filteredGroups, currentPage, itemsPerPage]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedAirlineFilter]);



  if (isAuthLoading) {
    return (
      <div className="overview-wrapper">
        <div className="admin-allowed-groups">
          <div className="loading">{LABELS.CHECKING_PERMISSIONS}</div>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="admin-content-wrapper">
        <div className="admin-access-denied">
          <h2>Access Denied</h2>
          <p>You do not have permission to access this page.</p>
          <p>Your email: {userEmail || 'Unknown'}</p>
          <p>Only specific authorized users can access the Admin Allowed Groups page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content-wrapper">
      <div className="admin-allowed-groups">
          <div className="admin-header">
            <div>
              <h1>{LABELS.MANAGE_ALLOWED_GROUPS}</h1>
              {/* <button 
                className="btn-back" 
                onClick={handleBackToOverview}
                type="button"
              >
                {LABELS.BACK_TO_OVERVIEW}
              </button> */}
            </div>
            <button 
              className="btn-primary" 
              onClick={() => handleOpenModal()}
              type="button"
            >
              {LABELS.ADD_NEW_GROUP}
            </button>
          </div>

          {error && <div className="alert alert-error">{error}</div>}

          <div className="filter-section">
            <label htmlFor="airlineFilter">{LABELS.FILTER_BY_AIRLINE}</label>
            <select 
              id="airlineFilter"
              value={selectedAirlineFilter}
              onChange={(e) => setSelectedAirlineFilter(e.target.value)}
              className="airline-filter"
            >
              <option value={FILTER_ALL}>{LABELS.ALL_AIRLINES}</option>
              {uniqueAirlines.map(airline => (
                <option key={airline} value={airline}>{airline}</option>
              ))}
            </select>
            <span className="group-count">
              {filteredGroups.length} group(s) found
            </span>
          </div>

          {loading ? (
            <div className="loading">{LABELS.LOADING}</div>
          ) : (
            <>
              <div className="groups-container">
                <div className="groups-section">
                  <table className="groups-table">
                    <thead>
                      <tr>
                        <th>Airline</th>
                        <th>{TABLE_HEADERS.GROUP_NAME}</th>
                        <th>{TABLE_HEADERS.STATUS}</th>
                        <th>{TABLE_HEADERS.CREATED_DATE}</th>
                        <th>{TABLE_HEADERS.MODIFIED_DATE}</th>
                        <th>{TABLE_HEADERS.ACTIONS}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedGroups.map(group => (
                        <tr key={group.id}>
                          <td><strong>{group.airline}</strong></td>
                          <td>{group.groupName}</td>
                          <td>
                            <label className="switch" aria-label={`Toggle ${group.groupName} status`}>
                              <input
                                type="checkbox"
                                checked={group.isActive}
                                onChange={() => handleToggleActive(group)}
                                aria-label={`${group.isActive ? 'Active' : 'Inactive'} status`}
                              />
                              <span className="slider"></span>
                            </label>
                            <span className={group.isActive ? 'status-active' : 'status-inactive'}>
                              {group.isActive ? STATUS_LABELS.ACTIVE : STATUS_LABELS.INACTIVE}
                            </span>
                          </td>
                          <td>{group.createdDate ? new Date(group.createdDate).toLocaleDateString() : '-'}</td>
                          <td>{group.modifiedDate ? new Date(group.modifiedDate).toLocaleDateString() : '-'}</td>
                          <td className="actions">
                            <button 
                              className="btn-edit" 
                              onClick={() => handleOpenModal(group)}
                              type="button"
                            >
                              {BUTTON_LABELS.EDIT}
                            </button>
                            <button 
                              className="btn-delete" 
                              onClick={() => group.id && handleDelete(group.id)}
                              type="button"
                            >
                              {BUTTON_LABELS.DELETE}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {paginatedGroups.length === 0 && (
                  <div className="no-groups">
                    <p>{LABELS.NO_GROUPS_FOUND}</p>
                  </div>
                )}
              </div>

              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Previous
                  </button>
                  <span className="pagination-info">
                    Page {currentPage} of {totalPages} ({filteredGroups.length} total)
                  </span>
                  <button 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}

          {isModalOpen && (
            <div 
              className="modal-overlay" 
              onClick={handleCloseModal}
              onKeyDown={(e) => e.key === 'Escape' && handleCloseModal()}
              role="presentation"
              tabIndex={-1}
            >
              <div 
                className="modal-content" 
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    e.stopPropagation();
                    handleCloseModal();
                  }
                }}
                role="dialog"
                aria-modal="true"
                tabIndex={0}
              >
                <div className="modal-header">
                  <h2>{editingGroup ? 'Edit Group' : 'Add New Group'}</h2>
                  <button className="modal-close" onClick={handleCloseModal} type="button">✕</button>
                </div>
                <form onSubmit={handleSubmit}>
                  {modalFeedback && (
                    <div
                      className={`alert ${modalFeedback.type === 'error' ? 'alert-error' : 'alert-success'}`}
                      role="alert"
                      aria-live="polite"
                    >
                      {modalFeedback.message}
                    </div>
                  )}

                  <div className="form-group">
                    <label htmlFor="groupName">Group Name *</label>
                    <input
                      type="text"
                      id="groupName"
                      value={formData.groupName}
                      onChange={(e) => setFormData({ ...formData, groupName: e.target.value })}
                      required
                      placeholder="e.g., AAD-ACARSHistoryASUsersAutoEnrollment"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="airline">Airline *</label>
                    <select
                      id="airline"
                      value={formData.airline}
                      onChange={(e) => setFormData({ ...formData, airline: e.target.value })}
                      required
                    >
                      {airlines.map(airline => (
                        <option key={airline} value={airline}>{airline}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.isActive}
                        onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                      />
                      Active
                    </label>
                  </div>

                  <div className="modal-footer">
                    <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                      {BUTTON_LABELS.CANCEL}
                    </button>
                    <button type="submit" className="btn-primary">
                      {editingGroup ? BUTTON_LABELS.UPDATE : BUTTON_LABELS.CREATE}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
  );
};

export default AdminAllowedGroups;
